import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Leaf } from 'lucide-react';
import { text, type Locale } from '../shared/content';
import { safeAssetPath, adAspectRatio, type AdDelivery } from './ads/useAdDelivery';
import { domVisibility } from './ads/visibility';
import { sponsorLabel } from './ads/billboard';

export default function AdSlot({
  placement,
  locale,
  delivery,
  project,
}: {
  placement: 'journal' | 'community';
  locale: Locale;
  delivery: AdDelivery | null;
  project?: { id: string; title: string };
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [imageState, setImageState] = useState('');
  const active = placement === 'community' && !project ? null : delivery;
  const campaign = active?.campaign;
  const path = safeAssetPath(campaign?.assetPath);
  const creativeKey = campaign ? `${campaign.creativeHash}:${campaign.creativeRevision}` : '';
  const latestCreativeKey = useRef(creativeKey);
  latestCreativeKey.current = creativeKey;
  const imageReady =
    !path || imageState === `loaded:${creativeKey}` || imageState === `failed:${creativeKey}`;
  const showImage = !!path && imageState !== `failed:${creativeKey}`;
  useEffect(() => {
    if (!active || !ref.current || !imageReady) return;
    let disposed = false;
    let painted = false;
    let frame = 0;
    const sample = () => {
      const element = ref.current;
      if (disposed || !element) return;
      const fraction = performance.now() < active.expiresAt ? domVisibility(element) : 0;
      active.measurement.observe(
        {
          painted: painted && fraction > 0,
          visibleFraction: fraction,
          foreground: document.visibilityState === 'visible',
        },
        performance.now(),
      );
    };
    // Image decode/load precedes two animation frames of real DOM painting.
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        painted = true;
        sample();
      });
    });
    const timer = setInterval(sample, 200);
    document.addEventListener('visibilitychange', sample);
    return () => {
      disposed = true;
      active.measurement.pause();
      cancelAnimationFrame(frame);
      clearInterval(timer);
      document.removeEventListener('visibilitychange', sample);
    };
  }, [active, creativeKey, imageReady]);
  return (
    <div
      className={`ad-slot ${campaign ? 'sponsored' : 'city-poster'}`}
      ref={ref}
      data-ad-placement={placement}
    >
      {project && (
        <p className="muted tiny" style={{ padding: '8px 14px', margin: 0 }}>
          {
            (campaign
              ? text(
                  'Public project sponsorship',
                  '公共项目赞助',
                  '公共專案贊助',
                  'Patrocinio del proyecto público',
                )
              : text('Public project', '公共项目', '公共專案', 'Proyecto público'))[locale]
          }{' '}
          · {project.title}
          <small style={{ display: 'block', overflowWrap: 'anywhere' }}>{project.id}</small>
        </p>
      )}
      {campaign ? (
        <a
          href={campaign.clickPath}
          target="_blank"
          rel="noopener noreferrer sponsored"
          referrerPolicy="no-referrer"
        >
          <small>
            {sponsorLabel[locale]} · {campaign.sponsor}
          </small>
          <strong>{campaign.title}</strong>
          {showImage && (
            <div
              style={{
                aspectRatio: adAspectRatio(campaign.aspect),
                width: '100%',
                maxWidth: 220 * adAspectRatio(campaign.aspect),
                margin: '10px auto 0',
                background: '#f4e8ce',
              }}
            >
              <img
                key={`${campaign.assetId}:${creativeKey}`}
                src={path!}
                alt={campaign.assetAlt}
                loading="lazy"
                style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
                onLoad={(event) => {
                  if (latestCreativeKey.current === creativeKey && event.currentTarget.naturalWidth > 0)
                    setImageState(`loaded:${creativeKey}`);
                }}
                onError={() => {
                  if (latestCreativeKey.current === creativeKey) setImageState(`failed:${creativeKey}`);
                }}
              />
            </div>
          )}
          <p>{campaign.body}</p>
          <ArrowUpRight size={16} />
        </a>
      ) : (
        <div>
          <Leaf size={23} />
          <span>
            <small>{text('Neighborhood notice', '街区公告', '街區公告', 'Aviso del barrio')[locale]}</small>
            <strong>
              {
                text(
                  'Good things grow together.',
                  '一起，让街区变得更好。',
                  '一起，讓街區變得更好。',
                  'Las cosas buenas crecen juntas.',
                )[locale]
              }
            </strong>
          </span>
        </div>
      )}
    </div>
  );
}
